import { SectionTabs } from '../components/ui/SectionTabs';
import { PageHeader } from '../components/ui/PageHeader';
import { Icon } from '../components/ui/Icon';
import { Button, Select } from '../components/ui/primitives';
import { CheckRow } from '../components/ui/CheckRow';
import { DenseList, DenseRow } from '../components/ui/DenseList';
import { useShellTier } from '../layouts/shellTier';
import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { createForm, deleteForm, listForms, setFormActive } from '../api/forms';
import { getShiftCloseConfig, removeShiftCloseBinding, setShiftCloseBinding } from '../api/formSubmissions';
import { getStores } from '../api/stores';
import { useAuth } from '../auth/AuthProvider';
import { canCreateShiftClose, canManageForms, canReviewFormResponses, canViewFormResponses } from '../auth/permissions';
import { Modal } from '../components/Modal';
import { FormBuilder } from '../components/FormBuilder';
import { FormResponsesPanel } from '../components/FormResponsesPanel';
import { FormSupervisionPanel } from '../components/FormSupervisionPanel';
import { useConfirm } from '../components/ui/ConfirmDialog';
import type { FormTemplateListItem } from '../types/forms';
import type { Store } from '../types/stores';
import { storeIdsForNewForm } from '../utils/formBuilderModel';

// Fase 23A: pantalla "Formularios y checklists". Lista los formularios del negocio, permite crear/
// activar/eliminar y abre el constructor. La seguridad real la aplica el backend; aqui se ocultan
// controles segun permiso (forms.manage).
type FormsView = 'list' | 'responses' | 'supervision';

export function FormsPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const canManage = canManageForms(user?.role);
  const canSupervise = canViewFormResponses(user?.role);
  const canReview = canReviewFormResponses(user?.role);
  const canOpenShiftClose = canCreateShiftClose(user?.role);
  // Fase 23C/25A-R: tres vistas dentro de "Formularios": administracion (lista/constructor),
  // respuestas (historial/evidencia) y supervision (analitica).
  // P-075: `#forms/supervision` o `#forms/respuestas` (el Inicio lleva a Supervisión) abren esa pestaña; la URL
  // vuelve a `#forms` sin navegar, como Reportes con sus segmentos.
  const [view, setView] = useState<FormsView>(() => {
    const second = window.location.hash.replace('#', '').split('/')[1];
    if (second) window.history.replaceState(window.history.state, '', '#forms');
    if (!canViewFormResponses(user?.role)) return 'list';
    return second === 'supervision' ? 'supervision' : second === 'respuestas' ? 'responses' : 'list';
  });
  // Fase 25A-R: al pulsar "Ver respuestas" de un formulario, se abre la pestaña con ese filtro fijado.
  const [responsesTemplateId, setResponsesTemplateId] = useState<string | undefined>();
  const [forms, setForms] = useState<FormTemplateListItem[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<FormTemplateListItem | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const tier = useShellTier();
  // Fase 23B: uso de cada formulario en el Corte de Turno (templateId -> REQUIRED/OPTIONAL).
  const [bindings, setBindings] = useState<Record<string, 'REQUIRED' | 'OPTIONAL'>>({});

  const load = useCallback(async () => {
    setIsLoading(true); setError(undefined);
    try {
      const [f, s, cfg] = await Promise.all([listForms({ includeInactive }), getStores(), getShiftCloseConfig()]);
      setForms(f.templates);
      setStores(s.stores);
      setBindings(Object.fromEntries(cfg.bindings.map((b) => [b.templateId, b.requirement])));
    } catch (e) { setError(getErrorMessage(e, 'No fue posible cargar los formularios.')); }
    finally { setIsLoading(false); }
  }, [includeInactive]);

  const changeBinding = async (form: FormTemplateListItem, value: '' | 'REQUIRED' | 'OPTIONAL') => {
    setError(undefined);
    try {
      const res = value === '' ? await removeShiftCloseBinding(form.id) : await setShiftCloseBinding(form.id, value);
      setBindings(Object.fromEntries(res.bindings.map((b) => [b.templateId, b.requirement])));
      setNotice('Configuracion de corte actualizada.');
    } catch (e) { setError(getErrorMessage(e, 'No fue posible actualizar el uso en el corte.')); }
  };

  useEffect(() => { void load(); }, [load]);

  const refreshKeepingSelection = useCallback(async () => {
    try {
      const f = await listForms({ includeInactive });
      setForms(f.templates);
      setSelected((sel) => (sel ? f.templates.find((t) => t.id === sel.id) ?? sel : sel));
    } catch { /* noop */ }
  }, [includeInactive]);

  const toggleActive = async (form: FormTemplateListItem) => {
    try { await setFormActive(form.id, !form.isActive); setNotice(form.isActive ? 'Formulario desactivado.' : 'Formulario activado.'); await load(); }
    catch (e) { setError(getErrorMessage(e, 'No fue posible cambiar el estado.')); }
  };

  const remove = async (form: FormTemplateListItem) => {
    if (!(await confirm({ title: 'Eliminar formulario', description: `Eliminar el formulario "${form.name}"? Esta acción no se puede deshacer.`, confirmLabel: 'Eliminar', danger: true }))) return;
    try { await deleteForm(form.id); setNotice('Formulario eliminado.'); await load(); }
    catch (e) { setError(getErrorMessage(e, 'No fue posible eliminar (los formularios con versiones publicadas solo pueden desactivarse).')); }
  };

  const storeLabel = (form: FormTemplateListItem) => {
    if (form.appliesToAllStores) return 'Todas las tiendas';
    const names = form.storeIds.map((id) => stores.find((s) => s.id === id)?.name ?? '—');
    return names.length ? names.join(', ') : 'Sin tiendas';
  };

  if (selected) {
    return <FormBuilder template={selected} stores={stores} onBack={() => { setSelected(null); void load(); }} onChanged={() => void refreshKeepingSelection()} />;
  }

  const tabs = canSupervise ? (
    <SectionTabs
      active={view}
      label="Vistas de Diseño de formularios"
      onSelect={(id) => { if (id === 'responses') setResponsesTemplateId(undefined); setView(id as FormsView); }}
      tabs={[{ id: 'list', label: 'Administración' }, { id: 'responses', label: 'Respuestas' }, { id: 'supervision', label: 'Supervisión' }]}
    />
  ) : null;

  if (canSupervise && view === 'responses') {
    return (
      <section aria-label="Respuestas de formularios" className="n3-screen">
        <PageHeader description="El historial de respuestas de tu equipo; abre cada una para ver su contenido completo tal como se registró." title="Diseño de formularios" />
        {tabs}
        <FormResponsesPanel stores={stores} templates={forms} initialTemplateId={responsesTemplateId} canOpenShiftClose={canOpenShiftClose} />
      </section>
    );
  }

  if (canSupervise && view === 'supervision') {
    return (
      <section aria-label="Supervisión de formularios" className="n3-screen">
        <PageHeader description="La actividad de tu equipo, los hallazgos que requieren atención y el análisis por tienda y formulario." title="Diseño de formularios" />
        {tabs}
        <FormSupervisionPanel stores={stores} canReview={canReview} />
      </section>
    );
  }

  return (
    <section aria-label="Formularios y checklists" className="n3-screen">
      <PageHeader
        actions={canManage ? <Button iconStart={<Icon name="plus" size={18} />} onClick={() => setCreating(true)} variant="primary">Crear formulario</Button> : undefined}
        description="Aquí diseñas los formularios y checklists; tu equipo los responde en Operación → Formularios (o dentro del Corte de turno, si los marcas como obligatorios del corte)."
        title="Diseño de formularios"
      />

      {tabs}

      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {notice && <div className="alert alert--success" role="status">{notice}</div>}

      <CheckRow checked={includeInactive} label="Mostrar inactivos" onChange={setIncludeInactive} plain />

      {isLoading ? <div className="inline-loading"><span className="spinner" />Cargando formularios…</div> : forms.length === 0 ? (
        <div className="empty-panel"><strong>Sin formularios</strong><span>{canManage ? 'Crea el primer formulario para comenzar.' : 'Aún no hay formularios configurados.'}</span></div>
      ) : tier === 'compact' ? (
        // P-076: en el teléfono, una fila compacta por formulario (nombre, estado, versiones, preguntas y tiendas; el
        // uso en el corte como control de la fila; las acciones a la vista). La tabla de ocho columnas sigue en
        // tableta y escritorio.
        <DenseList label="Formularios" separated>
          {forms.map((form) => (
            <DenseRow
              actions={<>
                {canManage ? (
                  <Select aria-describedby={form.activeVersion ? undefined : `form-binding-why-${form.id}`} aria-label={`Uso en corte de ${form.name}`} disabled={!form.activeVersion} onChange={(e) => void changeBinding(form, e.target.value as '' | 'REQUIRED' | 'OPTIONAL')} value={bindings[form.id] ?? ''}>
                    <option value="">Corte: no usar</option>
                    <option value="OPTIONAL">Corte: opcional</option>
                    <option value="REQUIRED">Corte: obligatoria</option>
                  </Select>
                ) : null}
                {/* P-078 (BK34): la razón del control deshabilitado, visible (no solo en un title). */}
                {canManage && !form.activeVersion && <span className="n3-row__why" id={`form-binding-why-${form.id}`}>Publica una versión para usarlo en el corte.</span>}
                <Button onClick={() => setSelected(form)} variant="ghost">{canManage ? 'Editar' : 'Ver'}</Button>
                {canSupervise && <Button onClick={() => { setResponsesTemplateId(form.id); setView('responses'); }} variant="ghost">Ver respuestas</Button>}
                {canManage && <Button onClick={() => void toggleActive(form)} variant={form.isActive ? 'danger' : 'ghost'}>{form.isActive ? 'Desactivar' : 'Activar'}</Button>}
                {canManage && !form.activeVersion && <Button onClick={() => void remove(form)} variant="danger">Eliminar</Button>}
              </>}
              badge={<span className={`form-badge ${form.isActive ? 'form-badge--active' : 'form-badge--inactive'}`}>{form.isActive ? 'Activo' : 'Inactivo'}</span>}
              inactive={!form.isActive}
              key={form.id}
              meta={[
                `Publicada: ${form.activeVersion ? `v${form.activeVersion.versionNumber}` : '—'}`,
                `Borrador: ${form.draft ? `v${form.draft.versionNumber}` : '—'}`,
                `${form.activeVersion?.questionCount ?? (form.draft ? '…' : 0)} pregunta(s)`,
                `Tiendas: ${storeLabel(form)}`,
                ...(canManage ? [] : [`Corte de turno: ${bindings[form.id] === 'REQUIRED' ? 'Obligatoria' : bindings[form.id] === 'OPTIONAL' ? 'Opcional' : '—'}`]),
              ]}
              name={form.name}
              subtitle={form.description || undefined}
              title={form.name}
            />
          ))}
        </DenseList>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead><tr><th>Nombre</th><th>Estado</th><th>Publicada</th><th>Borrador</th><th>Preguntas</th><th>Tiendas</th><th>Corte de turno</th><th>Acciones</th></tr></thead>
            <tbody>
              {forms.map((form) => (
                <tr key={form.id} className={form.isActive ? '' : 'data-row--inactive'}>
                  <td><strong>{form.name}</strong>{form.description && <small>{form.description}</small>}</td>
                  <td><span className={`form-badge ${form.isActive ? 'form-badge--active' : 'form-badge--inactive'}`}>{form.isActive ? 'Activo' : 'Inactivo'}</span></td>
                  <td>{form.activeVersion ? `v${form.activeVersion.versionNumber}` : '—'}</td>
                  <td>{form.draft ? `v${form.draft.versionNumber}` : '—'}</td>
                  <td>{form.activeVersion?.questionCount ?? (form.draft ? '…' : 0)}</td>
                  <td>{storeLabel(form)}</td>
                  <td>
                    {canManage ? (
                      <>
                        <select aria-describedby={form.activeVersion ? undefined : `form-binding-why-table-${form.id}`} aria-label={`Uso en corte de ${form.name}`} value={bindings[form.id] ?? ''} disabled={!form.activeVersion} onChange={(e) => void changeBinding(form, e.target.value as '' | 'REQUIRED' | 'OPTIONAL')}>
                          <option value="">No usar</option>
                          <option value="OPTIONAL">Opcional</option>
                          <option value="REQUIRED">Obligatoria</option>
                        </select>
                        {/* P-078 (BK34): la razón del control deshabilitado, visible. */}
                        {!form.activeVersion && <small className="n3-row__why" id={`form-binding-why-table-${form.id}`}>Publica una versión para usarlo en el corte.</small>}
                      </>
                    ) : (bindings[form.id] === 'REQUIRED' ? 'Obligatoria' : bindings[form.id] === 'OPTIONAL' ? 'Opcional' : '—')}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="text-button" onClick={() => setSelected(form)} type="button">{canManage ? 'Editar' : 'Ver'}</button>
                      {canSupervise && <button className="text-button" onClick={() => { setResponsesTemplateId(form.id); setView('responses'); }} type="button">Ver respuestas</button>}
                      {canManage && <button className="text-button" onClick={() => void toggleActive(form)} type="button">{form.isActive ? 'Desactivar' : 'Activar'}</button>}
                      {canManage && !form.activeVersion && <button className="text-button text-button--danger" onClick={() => void remove(form)} type="button">Eliminar</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* P-061: un MANAGER crea el formulario para SUS tiendas (lista explicita); el OWNER, como siempre, para todas. */}
      {creating && <CreateFormModal storeIds={storeIdsForNewForm(user?.role, stores)} onClose={() => setCreating(false)} onCreated={async (form) => { setCreating(false); setNotice('Formulario creado.'); await load(); setSelected(form); }} />}
    </section>
  );
}

function CreateFormModal({ storeIds, onClose, onCreated }: { storeIds: string[] | undefined; onClose: () => void; onCreated: (form: FormTemplateListItem) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const submit = async () => {
    if (busy || !name.trim()) return;
    setBusy(true); setError(undefined);
    try { const res = await createForm({ name: name.trim(), description: description.trim() || null, ...(storeIds ? { storeIds } : {}) }); onCreated(res.template); }
    catch (e) { setError(getErrorMessage(e, 'No fue posible crear el formulario.')); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Crear formulario" isBusy={busy} onClose={onClose} footer={<><button className="button button--ghost" disabled={busy} onClick={onClose} type="button">Cancelar</button><button className="button button--primary" disabled={busy || !name.trim()} onClick={() => void submit()} type="button">Crear</button></>}>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <label className="field"><span>Nombre *</span><input value={name} maxLength={150} onChange={(e) => setName(e.target.value)} autoFocus /></label>
      <label className="field"><span>Descripcion (opcional)</span><textarea value={description} maxLength={1000} rows={3} onChange={(e) => setDescription(e.target.value)} /></label>
    </Modal>
  );
}
